import { REDACTED } from "./secrets.ts";

export type RunStep = { tool: string; ok: boolean; object?: string };

export const MOST_STEPS = 40;

export const A_TOOL_NAME_IS_AT_MOST = 40;

export const AN_OBJECT_IS_AT_MOST = 48;

export const A_PROGRAM_IS_AT_MOST = 24;

const AN_OBJECT_IS_SPELLED_WITH = /^[A-Za-z0-9._\-/*?[\]{}~+#@!]+$/;

const A_SHELL = new Set(["bash", "sh", "zsh", "shell", "run", "exec", "execute", "command", "terminal"]);

const A_FETCH = new Set(["fetch", "web", "webfetch", "web_fetch", "http", "curl", "browse", "websearch"]);

const A_SEARCH = new Set(["grep", "glob", "search", "find", "rg", "ripgrep", "codebase_search"]);

const A_PATH_UNDER = ["path", "file_path", "filePath", "file", "notebook_path", "filename", "target"];
const A_PATTERN_UNDER = ["pattern", "glob", "query", "q"];
const A_URL_UNDER = ["url", "uri", "href", "link"];
const A_COMMAND_UNDER = ["command", "cmd", "script"];

function pick(bag: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const said = bag[key];
    if (typeof said === "string" && said.trim() !== "") return said;
  }
  return undefined;
}

const isControl = (code: number) => code < 0x20 || (code >= 0x7f && code <= 0x9f);

function clean(said: string): string {
  let out = "";
  for (const letter of said) out += isControl(letter.charCodeAt(0)) ? " " : letter;
  return out.trim();
}

export function looksLikeASecret(said: string): boolean {
  if (said.includes(REDACTED)) return true;
  if (/(^|[^A-Za-z0-9])(sk|pk|rk|api)[-_][A-Za-z0-9_-]{8,}/i.test(said)) return true;
  if (/(ghp|gho|ghu|ghs|github_pat|xox[abposr]|AKIA|ASIA|eyJ|BEGIN)/.test(said)) return true;

  for (const segment of said.split(/[/.]/)) {
    if (segment.length < 24) continue;
    if (/[A-Za-z]/.test(segment) && /[0-9]/.test(segment)) return true;
  }
  return false;
}

const CUT = "…";

function keepingTheTail(said: string, most: number): string {
  if (said.length <= most) return said;
  const window = said.slice(said.length - (most - 1));
  const at = window.indexOf("/");
  return `${CUT}${at === -1 ? window : window.slice(at)}`;
}

export function aPath(said: string | undefined): string | undefined {
  if (said === undefined) return undefined;
  const cleaned = clean(said);
  const wasCut = cleaned.startsWith(CUT);
  const body = wasCut ? cleaned.slice(CUT.length) : cleaned;
  if (!body || !AN_OBJECT_IS_SPELLED_WITH.test(body)) return undefined;
  if (looksLikeASecret(body)) return undefined;

  const kept = keepingTheTail(body, AN_OBJECT_IS_AT_MOST - (wasCut ? CUT.length : 0));
  return wasCut && !kept.startsWith(CUT) ? `${CUT}${kept}` : kept;
}

export function theProgram(said: string | undefined): string | undefined {
  if (said === undefined) return undefined;
  for (const word of clean(said).split(/\s+/)) {
    if (word.includes("=")) continue;
    const name = word.slice(word.lastIndexOf("/") + 1);
    if (!name || !/^[A-Za-z0-9._-]+$/.test(name) || looksLikeASecret(name)) return undefined;
    return name.slice(0, A_PROGRAM_IS_AT_MOST);
  }
  return undefined;
}

export function theHost(said: string | undefined): string | undefined {
  if (said === undefined) return undefined;
  const cleaned = clean(said);
  try {
    const host = new URL(cleaned).hostname;
    return host && !looksLikeASecret(host) ? host.slice(0, AN_OBJECT_IS_AT_MOST) : undefined;
  } catch {
    if (!/^[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(cleaned)) return undefined;
    return looksLikeASecret(cleaned) ? undefined : cleaned.slice(0, AN_OBJECT_IS_AT_MOST);
  }
}

export function anObject(tool: unknown, args: unknown): string | undefined {
  const name = (typeof tool === "string" ? tool : "").toLowerCase();
  const bag =
    args && typeof args === "object" && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
  if (A_SHELL.has(name)) return theProgram(pick(bag, A_COMMAND_UNDER));
  if (A_FETCH.has(name)) return theHost(pick(bag, A_URL_UNDER));
  if (A_SEARCH.has(name)) return aPath(pick(bag, A_PATTERN_UNDER) ?? pick(bag, A_PATH_UNDER));

  return aPath(pick(bag, A_PATH_UNDER));
}

export function aStep(tool: unknown, ok: unknown, object?: unknown): RunStep {
  const said = typeof tool === "string" ? tool : "";
  const name = said.replace(/[^\w.-]/g, "").slice(0, A_TOOL_NAME_IS_AT_MOST);
  const step: RunStep = { tool: name || "a tool", ok: ok === true };

  const aimed = typeof object === "string" ? aPath(object) : undefined;
  return aimed ? { ...step, object: aimed } : step;
}
